import Vue from 'vue';
import qs from 'qs';
import lodash from 'lodash';
import VueRouter from 'vue-router';
import store from '../store';
import LoginLayout from '../views/LoginLayout.vue';
import LoggedInLayout from '../views/LoggedInLayout.vue';
import NotFound from '../components/main-column/NotFound.vue';
import NetworkError from '../components/main-column/NetworkError.vue';
import moment from 'moment';

Vue.use(VueRouter);

const routes = [
  { path: '/pricing-guides', name: 'pricing-guides', component: LoggedInLayout, props: { componentName: 'PricingGuidesLayout' }, meta: { requiresAuth: true, title: 'Pricing Guides' } },
  { path: '/fuel-resources', name: 'fuel-resources', component: LoggedInLayout, props: { componentName: 'FuelResourcesLayout' }, meta: { requiresAuth: true, title: 'Fuel Resources' } },
  { path: '/online-learning-login', name: 'online-learning-login', component: LoggedInLayout, props: { componentName: 'OnlineLearningLoginLayout' }, meta: { requiresAuth: true, title: 'Online Learning Login' } },
  { path: '/vendor-partners', name: 'vendor-partners', component: LoggedInLayout, props: { componentName: 'VendorPartnersLayout' }, meta: { requiresAuth: true, title: 'Vendor Partners' } },
  { path: '/fuel-funds', name: 'fuel-funds', component: LoggedInLayout, props: { componentName: 'FuelFundsLayout' }, meta: { requiresAuth: true, title: 'Fuel Funds' } },
  { path: '/digital-promo', name: 'digital-promo', component: LoggedInLayout, props: { componentName: 'DigitalPromoLayout' }, meta: { requiresAuth: true, title: 'Digital Promo Funds' } },
  { path: '/allergy-kpis', name: 'allergy-kpis', component: LoggedInLayout, props: { componentName: 'AllergyKpisLayout' }, meta: { requiresAuth: true, title: 'Allergy KPIs' } },
  { path: '/audiology-kpis', name: 'audiology-kpis', component: LoggedInLayout, props: { componentName: 'AudiologyKpisLayout' }, meta: { requiresAuth: true, title: 'Audiology KPIs' } },
  { path: '/purchase-activity', name: 'purchase-activity', component: LoggedInLayout, props: { componentName: 'PurchaseActivityLayout' }, meta: { requiresAuth: true, title: 'Purchase Activity' } },
  { path: '/purchase-activity-flash', name: 'purchase-activity-flash', component: LoggedInLayout, props: { componentName: 'PurchaseActivityFlashLayout' }, meta: { requiresAuth: true, title: 'Flash Purchase Activity' } },
  { path: '/fuel-utilization', name: 'fuel-utilization', component: LoggedInLayout, props: { componentName: 'FuelUtilizationLayout' }, meta: { requiresAuth: true, title: 'Fuel Utilization' } },
  { path: '/battery-store', name: 'battery-store', component: LoggedInLayout, props: { componentName: 'BatteryStoreLayout' }, meta: { requiresAuth: true, title: 'Battery Store' } },
  { path: '/pay-invoice', name: 'pay-invoice', component: LoggedInLayout, props: { componentName: 'PayInvoiceLayout' }, meta: { requiresAuth: true, title: 'Pay Invoice' } },
  { path: '/meeting-agendas', name: 'meeting-agendas', component: LoggedInLayout, props: { componentName: 'MeetingAgendasLayout' }, meta: { requiresAuth: true, title: 'Meeting Agendas' } },
  { path: '/meeting-agendas/view/:id(\\d+)', name: 'meeting-agenda', component: LoggedInLayout, props: { componentName: 'MeetingAgendaLayout' }, meta: { requiresAuth: true, title: 'Meeting Agenda' } },
  { path: '/dashboard', name: 'dashboard', component: LoggedInLayout, props: { componentName: 'DashboardLayout' }, meta: { requiresAuth: true, title: 'Overview' } },
  { path: '/', redirect: '/dashboard' },

  { path: '/login', name: 'login', component: LoginLayout, props: { componentName: 'LoginForm' }, meta: { guest: true, title: 'Login' } },
  { path: '/verify', name: 'verify', component: LoginLayout, props: { componentName: 'VerifyForm', header: false }, meta: { guest: true, title: 'Account Verification' } },
  { path: '/reset', name: 'reset', component: LoginLayout, props: { componentName: 'ResetForm' }, meta: { guest: true, title: 'Password Reset' } },
  { path: '/authorize', name: 'authorize', component: LoginLayout, props: { componentName: 'AuthorizeForm' }, meta: { guest: true, title: 'Meeting Attendee Authorization' } },
  { path: '/:catchAll(.*)', name: 'NotFound', component: NotFound, meta: { guest: true, title: 'Not Found' } },
  { path: '/404/:resource', name: '404Resource', component: NotFound, props: true, meta: { guest: true, title: '404 Not Found' } },
  { path: '/network-error', name: 'NetworkError', component: NetworkError, meta: { guest: true, title: 'Network Error' } },
  { path: '/undefined', name: 'UndefinedError', component: NetworkError, meta: { guest: true, title: 'Unknown Error' } },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  // set custom query resolver
  parseQuery(query) {
    return qs.parse(query);
  },
  stringifyQuery(query) {
    const result = qs.stringify(query, { arrayFormat: 'brackets', encodeValuesOnly: true });
    return result ? (`?${result}`) : '';
  },
});

router.beforeEach((to, from, next) => {
  // Define public pages, and private (Everything else)
  const publicPages = ['/login', '/reset', '/verify/', '/verify', '/logout', '/authorize'];
  const authRequired = !publicPages.includes(to.path);

  // Check if data in localStorage is of an older format than what the App currently uses
  // Force refresh. Ignore if using fuel employee login since data will be automatically fresh then.
  let vcheck = store.getters['auth/userAppVersionIsCurrent']();
  if (!vcheck && !store.getters['main/fuelEmployee']) {

    // Grab current App version, and save to localStorage and set as current in-memory version.
    console.log("App versions do not match. Saving updated App Version in localStorage.");
    let latestAppVersion = store.getters['auth/appCurrentVersion'];
    localStorage.setItem('appVersion', latestAppVersion);
    store.dispatch('auth/setProperty',{ key: 'userAppVersion', value: latestAppVersion });

    // Deleted localStorage user data to force relog.
    console.log("Forcing relog because app version is out of date");
    localStorage.removeItem('user');
    localStorage.removeItem('userLastUpdate');
  }

  // Define variables
  let user = null;
  let loggedIn = localStorage.getItem('user');
  const attendeeAuthorized = localStorage.getItem('attendee');

  // If logged in user, parse info
  if (loggedIn) {
    user = JSON.parse(loggedIn);
  }

  // A user exists, the route needs auth, and the user does not have a token.
  // Or the user has no children array (which will always at least include the business itself)
  if (authRequired && user && (!user.hasOwnProperty('token') || !user.children)) {
    console.log('Deleting localStorage user');
    localStorage.removeItem('user');
    loggedIn = localStorage.getItem('user');
  }

  if (!authRequired && to.path !== '/authorize') {
    //localStorage.clear();
    localStorage.removeItem('user');
    localStorage.removeItem('userLastUpdate');
  }

  if (to.meta.hasOwnProperty('title')) {
    document.title = `Fuel Member Portal | ${to.meta.title}`;
  }

  if (to.query.hasOwnProperty('fuel_employee')) {
    store.dispatch('auth/loginFuelEmployee', to.query.fuel_employee)
      .then(() => next('/dashboard'))
      .catch(() => next('/login'));
    return;
  }

  if (to.query.hasOwnProperty('access_code')) {
    store.dispatch('auth/setProperty', { key: 'access_code', value: to.query.access_code });
    localStorage.setItem('access_code', to.query.access_code);
    next('/authorize');
    return;
  }

  if (to.name === 'meeting-agenda') {
    if (attendeeAuthorized && attendeeAuthorized !== 'null') {
      store.dispatch('auth/reloadAttendeeData', attendeeAuthorized);
      next();
      return;
    }
  }

  // if a meeting attendee is trying to access something else, we'll catch it and redirect to something they can view
  if (attendeeAuthorized && attendeeAuthorized !== 'null') {
    const attendee = JSON.parse(attendeeAuthorized);
    if (!lodash.map(attendee.permissions, 'url').includes(to.path.replace(/^\//, ''))) {
      store.dispatch('flash/addMessage', {
        type: 'error',
        text: 'Redirecting: You don\'t have access to that area of the member Portal.',
      });
      next(`/${lodash.map(attendee.permissions, 'url')[0]}`);
      return;
    }
  }

  if (authRequired && (!loggedIn || loggedIn === null || loggedIn === 'null' || loggedIn === undefined)) {
    next('/login');
    return;
  }

  if (authRequired && user) {
    // If it's been more than 48hrs since last data refresh, fetch fresh from server
    let currentTime = moment();
    let lastUpdateTime = store.getters['auth/userLastUpdate']();
    //console.log(currentTime);
    //console.log(lastUpdateTime);

    // If it's been over 24 hours, reload Member data from Fuel (to update Invoice options, etc)
    if (currentTime.diff(lastUpdateTime, 'h') > 24) {
      console.log('FULLY RELOADING DATA');
      store.dispatch('auth/fullReloadUserData', loggedIn);
    }

    // Otherwise reload data from localStorage into app state.
    else {
      console.log("NOT FULLY RELOADING DATA");
      store.dispatch('auth/reloadUserData', loggedIn);
    }
  }


  if (loggedIn) {
    const user = JSON.parse(loggedIn);
    // this is a hack. Still trying to find a more succinct way to handle meeting-agendas and meeting-agenda with one permission
    if ((to.name === 'meeting-agenda' && lodash.map(user.permissions, 'url').includes('meeting-agendas'))) {
      next();
      return;
    }
    if (to.name === 'online-learning-login') {
      next();
      return;
    }
    if ((to.path !== '/login' && to.path !== '/dashboard' && to.path !== '/' && !lodash.map(user.permissions, 'url').includes(to.path.replace(/^\//, '').replace(/$\//, '')))) {
      // store.dispatch('flash/addMessage', {
      //   type: 'error',
      //   text: 'Redirecting: You don\'t have access to that area of the member Portal.',
      // });
      next('/dashboard');
      return;
    }
  }
  next();
});

router.afterEach((to, from) => {
  if (to.path !== from.path) {
    store.dispatch('main/setProperty', { key: 'globalLoading', value: true });
  }
});

export default router;
